import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SocketMessage } from '../api/types';
import {
  WS_VEHICLE_TIME_END,
  WS_VEHICLE_NOTES_UPDATE,
  WS_NEW_PROXY,
  WS_VEHICLE_DETAILS_UPDATE,
  WS_NEW_BID,
  WS_NEW_ALTERNATIVE_BID_OFFER,
  WS_NEW_ALTERNATIVE_OFFER,
  WS_AUCTION_END
} from 'constants/actionTypes';
import { salesAdapter, salesApiSlice as api, useGetMySalesQueryState } from 'services/api/sales';
import getSocket from '.';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useGetActiveAuctionsQuery } from 'services/api/live-auction';

const useGetSelectedAuctionId = () => {
  const { data } = useGetActiveAuctionsQuery();
  const { filters } = useSelector((state: any) => state.mySales);
  if (!data) return null;
  return data?.[filters.auctionType as keyof typeof data]?.id || null;
};

export const useMySalesSocketEffect = () => {
  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;
  const { page, perPage, filters } = useSelector((state: any) => state.mySales);
  const user = useSelector((state: any) => state.user.user);

  const { data } = useGetMySalesQueryState({
    limit: perPage,
    offset: page,
    ...filters
  });

  const auctionId = useGetSelectedAuctionId();

  const listener = ({ payload, type }: SocketMessage) => {
    if (type === WS_AUCTION_END && payload.id === auctionId) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.removeAll(draft.rows);
            draft.count = 0;
          }
        )
      );

      dispatch(
        api.util.updateQueryData('getMySalesCounters', filters.auctionType, counterDraft => {
          counterDraft.pending = 0;
          counterDraft.active = 0;
        })
      );
    }

    if (type === WS_NEW_BID) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, {
              id: payload.vehicle_id,
              changes: {
                highest_bid: payload
              }
            });
          }
        )
      );
    }

    if (type === WS_NEW_PROXY) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, {
              id: payload.vehicle_id,
              changes: {
                proxy_bid: payload
              }
            });
          }
        )
      );
    }

    if (type === WS_VEHICLE_DETAILS_UPDATE) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, { id: payload.id, changes: payload });
          }
        )
      );
    }

    if (type === WS_VEHICLE_NOTES_UPDATE) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, {
              id: payload.vehicleId,
              changes: { notes: payload.notes }
            });
          }
        )
      );
    }

    if (type === WS_VEHICLE_TIME_END) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            const exists = salesAdapter.getSelectors().selectById(draft.rows, payload.id);
            const isMyVehicle = user?.id === payload.user_id;
            const isSelectedAuction = payload.auction_id === auctionId;
            if (!isMyVehicle || !isSelectedAuction) return;
            const shouldRemove = filters.auctionStatus === 'active';
            if (shouldRemove && exists) {
              salesAdapter.removeOne(draft.rows, payload.id);
              draft.count -= 1;
            } else {
              salesAdapter.upsertOne(draft.rows, payload);
            }

            dispatch(
              // @ts-ignore
              api.util.updateQueryData('getMySalesCounters', filters.auctionType, counterDraft => {
                counterDraft.pending += 1;
                counterDraft.active = Math.max(counterDraft.active - 1, 0);
              })
            );
          }
        )
      );
    }

    if (type === WS_NEW_ALTERNATIVE_BID_OFFER) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, {
              id: payload.vehicle_id,
              changes: {
                top_alternative_bid_offer: payload
              }
            });
          }
        )
      );
    }

    if (type === WS_NEW_ALTERNATIVE_OFFER) {
      dispatch(
        api.util.updateQueryData(
          'getMySales',
          {
            limit: perPage,
            offset: page,
            ...filters
          },
          draft => {
            salesAdapter.updateOne(draft.rows, {
              id: payload.vehicle_id,
              changes: {
                active_alternative_offer: payload.amount ? payload : null
              }
            });
          }
        )
      );
    }
  };

  useEffect(() => {
    if (user.role !== 'seller') return;
    if (!data) return;
    const vehicleIds = data?.rows?.ids ?? [];
    const socket = getSocket();

    socket.emit('subscribe', {
      prefix: `vehicles`,
      ids: vehicleIds
    });
    socket.emit('subscribe', {
      prefix: `vehicles-notes:${user.id}`,
      ids: vehicleIds
    });

    if (auctionId) {
      socket.emit('subscribe', `auctions:${auctionId}`);
    }

    socket.on('message', listener);

    return () => {
      // socket.emit('unsubscribe', {
      //   prefix: `vehicles`,
      //   ids: vehicleIds
      // });
      // socket.emit('unsubscribe', {
      //   prefix: `vehicles-notes:${user.id}`,
      //   ids: vehicleIds
      // });
      socket.off('message', listener);
    };
  }, [dispatch, data?.rows?.ids, perPage, page, filters, user.id]);
};
